import * as THREE from "three";
import {OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { gsap } from "gsap";
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { BoxGeometry, DoubleSide, MeshBasicMaterial, MultiplyBlending, Object3D, PlaneGeometry, ShaderMaterial, SubtractiveBlending, Vector3 } from "three";
import { BasicHatchingMaterial } from "./customMaterials/BasicHatchingMaterial";
import vCloudsShader from './shaders/cloud_vert.glsl'
import fCloudsShader from './shaders/cloud_frag.glsl'
import vSkyShader from './shaders/sky_vert.glsl'
import fSkyShader from './shaders/sky_frag.glsl'
import vTitleShader from './shaders/title_vert.glsl'
import fTitleShader from './shaders/title_frag.glsl'

export class World {
    constructor({canvas, assets}){
        this.windowWidth = window.innerWidth
        this.windowHeight = window.innerHeight
        this.pixelRatio = window.devicePixelRatio
        this.textures = assets.textures;
        this.model = assets.model

        // scene
        this.scene = new THREE.Scene()
        this.bgrColor = 0x000000
        this.fog = new THREE.Fog(this.bgrColor, 6, 8)
        //this.scene.fog = this.fog

        // camera
        this.camera = new THREE.PerspectiveCamera(30, window.innerWidth / window.innerHeight, .1, 80)
        this.camera.position.set(0, 0, 100)

        // renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            preserveDrawingBuffer: true
        });
        this.renderer.setClearColor(this.bgrColor)
        this.renderer.setPixelRatio(this.pixelRatio)
        this.renderer.setSize(this.windowWidth, this.windowHeight)
        this.renderer.outputEncoding = THREE.sRGBEncoding
        this.renderer.toneMapping = THREE.LinearToneMapping
        this.renderer.toneMappingExposure = 1
        this.renderer.shadowMap.enabled = true
        this.renderer.shadowMap.type = THREE.PCFShadowMap

        // orbit controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement)
        this.controls.enablePan = true
        this.controls.minDistance = 0
        this.controls.maxDistance = 50
        this.controls.addEventListener( 'change', () => {this.controlsChanged()} );

        // Clock
        this.clock = new THREE.Clock()
        this.time = 0

        // Mouse
        this.mouse = new THREE.Vector2()

        // Steps
        this.cameras = []
        this.targets = []
        this.focuses = []
        this.steps = 0
        this.currentStep = -1
        this.tempFocus = 0
        this.tempTarget = new THREE.Vector3()
        this.nextTarget = new THREE.Vector3()
        this.nextCamPosition = new THREE.Vector3()
        this.breathCamera = new THREE.Vector3()
        this.transition = false

        this.foliageEmitters = []
        this.foliagePositions = []
        this.foliageRotations = []
        this.foliageScales = []
        this.foliageTimes = []
        this.foliageLifes = []
        this.foliageDelays = []
        this.foliageSpeeds = []
        this.leavesCount = 2000


        //postProcessing
        this.initPostprocessing()
        
        // gltf
        this.processModel();

        // leaves
        this.createLeaves()
    }

    createLeaves(){
        const geometryLeaf = this.leaf.geometry.clone();
        const materialLeaf = this.leaf.material.clone();
        materialLeaf.side = DoubleSide
        this.leaves = new THREE.InstancedMesh( geometryLeaf, materialLeaf, this.leavesCount );

        for ( let i = 0; i < this.leavesCount; i ++ ) {
            this.resetFoliage(i)    
        }
        this.scene.add(this.leaves)
    }

    resetFoliage(id){
        const tau = 6.28
        this.dummy = new Object3D();
        const emittersCount = this.foliageEmitters.length
        const distToCenter = .3
        const emitter = this.foliageEmitters[id % emittersCount]
        this.foliagePositions[id] = new Vector3();
        this.dummy.position.set(emitter.position.x, emitter.position.y, emitter.position.z)

        this.foliagePositions[id].x = emitter.position.x + distToCenter * (-.5 + Math.random())
        this.foliagePositions[id].y = emitter.position.y + distToCenter * (-.5 + Math.random())
        this.foliagePositions[id].z = emitter.position.z + distToCenter * (-.5 + Math.random())

        this.dummy.rotation.x = Math.random()*tau
        this.dummy.rotation.y = Math.random()*tau
        this.dummy.rotation.z = Math.random()*tau
        
        const initScale = 1 + Math.random()*.5
        this.dummy.scale.set(initScale,initScale,initScale)
        this.dummy.updateMatrix();
        this.leaves.setMatrixAt( id, this.dummy.matrix );
        
        this.foliageRotations[id] = this.dummy.rotation.clone()
        this.foliageScales[id] = initScale
        this.foliageTimes[id] = 0 // total time counter
        this.foliageLifes[id] = 6 + Math.random() * 3 //flying duration
        this.foliageDelays[id] = Math.random() * 3
        this.foliageSpeeds[id] = new Vector3(.1 + Math.random(), .1 + Math.random(), .1 + Math.random())
    }
    
    updateLeaves(dt){
        for ( let i = 0; i < this.leavesCount; i ++ ) {
            if (this.foliageTimes[i] < this.foliageDelays[i]){
                this.foliageTimes[i] += dt
                continue
            }

            if (this.foliageTimes[i] > this.foliageDelays[i] + this.foliageLifes[i]){
                this.resetFoliage(i)
                continue
            }
            this.dummy.scale.set(this.foliageScales[i], this.foliageScales[i], this.foliageScales[i])
            this.dummy.position.copy(this.foliagePositions[i])
            this.dummy.rotation.copy(this.foliageRotations[i])
            this.dummy.position.x += dt * this.foliageSpeeds[i].x * .2
            this.dummy.position.y += dt * this.foliageSpeeds[i].y * .2
            this.dummy.rotation.y += dt * this.foliageSpeeds[i].x * .5
            this.dummy.rotation.z += dt * this.foliageSpeeds[i].z * .8
            this.dummy.updateMatrix();
            this.leaves.setMatrixAt( i, this.dummy.matrix );
            this.foliagePositions[i] = this.dummy.position.clone()
            this.foliageRotations[i] = this.dummy.rotation.clone()
            this.foliageTimes[i] += dt
            this.foliageScales[i] -= dt * .1;
        }
        this.leaves.instanceMatrix.needsUpdate = true

    }

    processModel(){
        this.scene.add(this.model.scene)
        this.steps = this.model.cameras.length
        
        // MOUNTAIN

        this.mountain = this.model.scene.getObjectByName('mountain')
        
        this.mountain.material = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.mountain.material.map,
            hatchingScale : 30,
            gamma : 2.8,
            whiteLevel: 1.2,
            blackLevel: -.1,
        })

        // MONSTER
        
        this.monster = this.model.scene.getObjectByName('monster')
        this.monster.material = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.monster.material.map,
            hatchingScale : 30,
            gamma : 2.5
        })

        // BUILDINGS
        
        this.buildings = this.model.scene.getObjectByName('buildings')
        this.buildings.material = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.buildings.material.map,
            hatchingScale : 30,
            gamma : 2.,
            whiteLevel: 1.5,
            blackLevel: -.2,
        })

        // CHARACTERS

        this.king = this.model.scene.getObjectByName('king')
        this.queen = this.model.scene.getObjectByName('queen')
        this.rabbits = this.model.scene.getObjectByName('rabbits')
        const charactersShader = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.king.material.map,
            hatchingScale : 30,
            gamma : 2.2,
            whiteLevel: 1.1,
            blackLevel: -.1,
        })
        this.king.material = charactersShader;
        this.queen.material = charactersShader;
        this.rabbits.material = charactersShader;

        // DECO
        
        this.deco = this.model.scene.getObjectByName('deco')
        this.deco.material = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.deco.material.map,
            hatchingScale : 30,
        })

        // BUILDINGS
        
        this.socle = this.model.scene.getObjectByName('socle')
        this.socle.material = new BasicHatchingMaterial({
            hatchingMap : this.textures.hatchC,
            map : this.socle.material.map,
            hatchingScale : 30,
            gamma : 2.
        })

        // SKY
        
        this.sky = this.model.scene.getObjectByName('sky')
        this.mapSky = this.model.scene.getObjectByName('sky').material.map

        this.sky.material = new ShaderMaterial({
            uniforms: {
                map : {value : this.mapSky},
                noiseMap : {value : this.textures.noise},
                hatchingScale: { value: 30 },
                hatchingMap: { value: this.textures.hatchC },
                mixDiffuse: { value: 0 },
                gamma: { value: 2.2 },
                whiteLevel: { value: 1.4 },
                blackLevel: { value: -.1 },
                time: {value: 0},
            },
            vertexShader: vSkyShader,
            fragmentShader: fSkyShader,

        })

        // Clouds
        this.mapClouds = this.model.scene.getObjectByName('cloud').material.map
        this.matClouds = new ShaderMaterial({
            uniforms: {
                map : {value : this.mapClouds},
                hatchingScale: { value: 10 },
                hatchingMap: { value: this.textures.hatchC },
                mixDiffuse: { value: 0 },
                gamma: { value: 1.5 },
                blackLevel: { value: 0 },
                whiteLevel: { value: 1 },
                time: {value: 0},
            },
            vertexShader: vCloudsShader,
            fragmentShader: fCloudsShader,
            side:DoubleSide,
            transparent: true,
        })

        this.model.scene.traverse(mesh => {
            if (mesh.name.indexOf("cloud")>-1) {
                mesh.material = this.matClouds
            }else if(mesh.name.indexOf("foliageEmitter")>-1) {
                this.foliageEmitters.push(mesh);
            }
        })

        // TITLE

        this.title = this.model.scene.getObjectByName('titlePlane')
        this.mapTitle = this.title.material.map

        this.title.material = new ShaderMaterial({
            uniforms: {
                map : {value : this.mapTitle},
                noiseMap : {value : this.textures.noise},
                hatchingScale: { value: 10 },
                hatchingMap: { value: this.textures.hatchC },
                mixDiffuse: { value: 0 },
                gamma: { value: 2.2 },
                whiteLevel: { value: 1 },
                blackLevel: { value: 0 },
                time: {value: 0},
            },
            vertexShader: vTitleShader,
            fragmentShader: fTitleShader,
            transparent: true,
        })

        // LEAF
        this.leaf = this.model.scene.getObjectByName('leaf')

        for (let i=0; i<this.steps; i++){
            const camera = this.model.cameras.find(x => x.name === "Camera"+i);
            const target = this.scene.getObjectByName( "target"+i );
            //const focus = this.scene.getObjectByName( "focus"+i );
            this.targets.push(target);
            this.cameras.push(camera);
            this.focuses.push(focus);
        }

        this.nextStep()
    }

    render(){
        this.dt = this.clock.getDelta()
        this.time += this.dt
        this.renderer.render(this.scene, this.camera); 
        if (this.matClouds){
            this.matClouds.uniforms.time.value = this.time
        }
        if (this.sky){
            this.sky.material.uniforms.time.value = this.time
        }
        if (this.title){
            this.title.material.uniforms.time.value = this.time
        }
        if (this.leaves){
            this.updateLeaves(this.dt)
        }
        
        if (!this.transition){
            const distanceToCam = this.camera.position.distanceTo(this.tempTarget);
            this.breathCamera.x = Math.sin(this.time *.5)*.00003 * distanceToCam
            this.breathCamera.z = Math.cos(this.time *.5)*.00003 * distanceToCam
            this.breathCamera.y = Math.sin(this.time)*.00005 * distanceToCam
            this.controls.target.addVectors(this.tempTarget, this.breathCamera)
            this.camera.lookAt(this.controls.target)
        }
        //this.postprocessing.composer.render(.1);
    }

    resize(winWidth, winHeight){
        this.width = winWidth
        this.height = winHeight
        this.camera.aspect = winWidth / winHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(winWidth, winHeight);
    }
    
    mouseMove(mousePos){
        this.mouse.x = mousePos.px;
		this.mouse.y = - mousePos.py; 
    }

    previousStep(){
        this.currentStep--;
        this.currentStep = (this.currentStep < 0) ?  this.steps-1 : this.currentStep;
        this.updateStep();
    }
    
    nextStep(){
        this.currentStep++;
        this.currentStep = (this.currentStep > this.steps-1) ? 0 : this.currentStep;
        this.updateStep();
    }

    updateStep(){

        const camPos = this.cameras[this.currentStep].position;
        const target = this.targets[this.currentStep].position;
        //const focusPos = this.focuses[this.currentStep].position;
        //const focusScale = this.focuses[this.currentStep].scale.x;

        gsap.to(this.tempTarget, {
            x: target.x,
            y: target.y,
            z: target.z,
            duration : 3,
            ease: "Power2.easeInOut",
        })

        gsap.to(this.camera.position, {
            x: camPos.x,
            y: camPos.y,
            z: camPos.z,
            duration : 3,
            ease: "Power2.easeInOut",

            onUpdate: ()=>{
                this.camera.lookAt(this.tempTarget)
                this.controls.target = this.tempTarget
                this.transition = true
                //this.postprocessing.bokeh.uniforms[ 'focus' ].value = this.tempTarget.distanceTo(this.camera.position)
            },

            onComplete: () => {
                this.transition = false
            }
        })
        //console.log(target.distanceTo(camPos));
    }

    controlsChanged(){
        this.postprocessing.bokeh.uniforms[ 'focus' ].value = this.tempTarget.distanceTo(this.camera.position)
    }

    initPostprocessing() {
        this.postprocessing = {}
        this.renderPass = new RenderPass( this.scene, this.camera );

        this.bokehPass = new BokehPass( this.scene, this.camera, {
            focus: 1.0,
            aperture: .001,
            maxblur: 0.005,

            width: this.windowWidth,
            height: this.windowHeight
        } );

        this.composer = new EffectComposer( this.renderer );

        this.composer.addPass( this.renderPass );
        this.composer.addPass( this.bokehPass );

        this.postprocessing.composer = this.composer;
        this.postprocessing.bokeh = this.bokehPass;
    }


}